import type { ErrorKey, LocaleTexts, LogMessage, NameOf } from '../i18n.js'
import type { Card, CardColor, CardKind } from '../cards.js'
import type { Army, GameEndReason } from '../state.js'

/** German texts produced by the server (game log, errors). */

/** Plural of a counted word, German rule (only 1 is singular); German plurals are irregular, so both forms are given. */
function n(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`
}

const cardColors: Record<CardColor, string> = { red: 'Rot', blue: 'Blau', green: 'Grün', yellow: 'Gelb' }

const cardKinds: Record<CardKind, string> = {
  number: 'Zahl',
  zero: '0',
  skip: 'Aussetzen',
  reverse: 'Richtungswechsel',
  plus2: '+2',
  wild: 'Joker',
  wild4: '+4',
}

function cardLabel(card: Card): string {
  const color = card.color ? cardColors[card.color] : ''
  switch (card.kind) {
    case 'number':
      return `${color} ${card.value}`
    case 'zero':
      return `${color} 0`
    case 'skip':
    case 'reverse':
    case 'plus2':
      return `${color} ${cardKinds[card.kind]}`
    case 'wild':
    case 'wild4':
      return cardKinds[card.kind]
  }
}

const winReasons: Partial<Record<GameEndReason, string>> = {
  king_captured: 'Schlagen des gegnerischen Königs',
  checkmate: 'Schachmatt',
  resign: 'Aufgabe des Gegners',
  abandon: 'Spielverlust des Gegners (längerer Verbindungsabbruch oder Inaktivität)',
}

function log(m: LogMessage, name: NameOf, card: (cardId: string) => string): string {
  const army = (a: Army) => (a === 'w' ? 'Weiß' : 'Schwarz')
  const moves = (count: number) => n(count, 'Zug', 'Züge')
  switch (m.id) {
    case 'game_start':
      return `Die Partie beginnt: ${name(m.white)} hat Weiß, ${name(m.black)} hat Schwarz.`
    case 'deck_reshuffled':
      return `Der Ablagestapel wird zu einem neuen Nachziehstapel gemischt (${m.count} Karten).`
    case 'deck_empty':
      return `${name(m.player)} kann keine Karte ziehen: Es sind keine Karten mehr übrig.`
    case 'card_drawn':
      return `${name(m.player)} zieht eine Karte und deckt ${card(m.cardId)} auf.`
    case 'turn_start':
      return `Runde ${m.turn}: ${name(m.player)} spielt ${army(m.army)}.`
    case 'number_card':
      return `${name(m.player)} spielt in dieser Runde ${moves(m.moves)}.`
    case 'zero_card':
      return `0: ${name(m.player)} setzt diese Runde ohne Zug aus.`
    case 'skip_card':
      return `Aussetzen: ${name(m.player)} muss aussetzen, ${name(m.next)} zieht eine Karte und spielt.`
    case 'skip_in_check':
      return `Aussetzen: ${name(m.player)} steht im Schach, darf nicht aussetzen und spielt 1 Zug zur Abwehr.`
    case 'recovery_in_check':
      return `${name(m.player)} steht weiterhin im Schach und spielt 1 Zug zur Abwehr.`
    case 'no_pawn_to_recover':
      return `${card(m.cardId)}: kein Bauer für ${army(m.army)} zurückzuholen${m.noSquare ? ' (kein freies Feld)' : ''}.`
    case 'pawns_recovered':
      return `${card(m.cardId)}: ${name(m.player)} holt ${n(m.count, 'Bauern', 'Bauern')} zum Einsetzen zurück.`
    case 'reverse_card':
      return `Richtungswechsel: ${name(m.white)} führt jetzt Weiß und ${name(m.black)} Schwarz. ${name(m.player)} spielt mit ${army(m.army)}.`
    case 'joker_card':
      return `Joker: ${name(m.player)} darf eine beliebige Figur wie eine Dame ziehen (1 Zug).`
    case 'effect_chosen':
      return `${name(m.player)} wählt ${moves(m.moves)}.`
    case 'pawn_placed':
      return `${name(m.player)} setzt einen Bauern auf ${m.square}.`
    case 'no_move_turn_ends':
      return `${name(m.player)} hat keinen möglichen Zug: Die Runde endet.`
    case 'turn_end':
      return `Ende der Runde ${m.turn}.`
    case 'draw_agreed':
      return 'Remis: Beide Spieler haben sich auf ein Remis geeinigt.'
    case 'draw_no_moves':
      return `Remis: ${m.turns} Runden in Folge ohne Figurenzug.`
    case 'win':
      return `${name(m.winner)} gewinnt durch ${winReasons[m.reason] ?? m.reason}.`
    case 'move':
      return `${name(m.player)} (${army(m.army)}) spielt ${m.san}.`
    case 'series_stopped_threat':
      return 'Der gegnerische König wird angegriffen: Die Zugserie endet.'
    case 'no_move_left':
      return `${name(m.player)} hat keinen möglichen Zug mehr.`
    case 'series_ended':
      return `${name(m.player)} beendet die Zugserie.`
    case 'draw_offered':
      return `${name(m.player)} bietet Remis an.`
    case 'draw_declined':
      return `${name(m.player)} lehnt das Remis ab.`
  }
}

type Params = Record<string, string | number>

const errors: Record<ErrorKey, string | ((p: Params) => string)> = {
  game_over: 'Die Partie ist beendet.',
  server_only: 'Dieser Befehl ist dem Server vorbehalten.',
  forfeit_needs_player: 'Ein Spielverlust muss den Spieler nennen (applyForfeit verwenden).',
  draw_already_offered: 'Es liegt bereits ein Remisangebot vor.',
  no_draw_to_accept: 'Es gibt kein Remisangebot zum Annehmen.',
  no_draw_to_decline: 'Es gibt kein Remisangebot zum Ablehnen.',
  not_your_turn: 'Du bist nicht an der Reihe.',
  no_effect_pending: 'Es steht kein Effekt aus.',
  no_effect_to_choose: 'Es gibt keinen Effekt zu wählen.',
  choose_moves: (p) => `Wähle zwischen 1 und ${p.max} Zügen.`,
  no_pawn_to_place: 'Es gibt keinen Bauern zum Einsetzen.',
  invalid_pawn_square: 'Auf dieses Feld darf kein Bauer gesetzt werden.',
  cannot_place_pawn: 'Der Bauer kann hier nicht eingesetzt werden.',
  not_move_phase: 'Das ist nicht die Zugphase.',
  invalid_move: 'Dieser Zug ist nicht erlaubt.',
  king_attacked_square: 'Der König darf nicht auf ein angegriffenes Feld ziehen.',
  king_left_in_check: 'Dieser Zug würde deinen König im Schach lassen.',
  king_not_capturable: 'Im Purist-Modus kann der König nicht geschlagen werden.',
  first_move_required: 'Der erste Zug ist Pflicht.',
  no_series: 'Keine Zugserie im Gang.',
  unknown_command: 'Unbekannter Befehl.',
  invite_unknown: 'Unbekannter Einladungscode.',
  game_not_found: 'Partie nicht gefunden.',
  game_full: 'Diese Partie ist bereits voll.',
  not_in_game: 'Du spielst in dieser Partie nicht mit.',
  game_not_started: 'Die Partie hat noch nicht begonnen.',
  game_not_finished: 'Die Partie ist noch nicht beendet.',
  stale_command: (p) => `Veralteter Befehl: erwartete Version ${p.expected}, erhalten ${p.received}.`,
  command_id_reused: 'Befehls-ID bereits verwendet.',
  view_unavailable: 'Ansicht nicht verfügbar.',
  cannot_challenge_self: 'Du kannst dich nicht selbst herausfordern.',
  player_not_found: 'Spieler nicht gefunden.',
  rate_limited: 'Zu viele Anfragen, versuch es gleich noch einmal.',
  invalid_request: 'Ungültige Anfrage.',
  internal: 'Interner Serverfehler.',
  server_unreachable: 'Der Server antwortet nicht.',
  username_taken: 'Dieser Benutzername ist bereits vergeben.',
  bad_credentials: 'Benutzername oder Passwort ist falsch.',
  too_many_attempts: 'Zu viele Versuche, versuch es in einer Minute noch einmal.',
  invalid_data: 'Ungültige Daten.',
  username_format: 'Benutzername: 3 bis 20 Buchstaben, Ziffern, „_“, „-“ oder „.“.',
  password_length: 'Passwort: mindestens 6 Zeichen.',
  credentials_required: 'Benutzername und Passwort sind erforderlich.',
  invalid_session: 'Ungültige Sitzung.',
  match_not_ranked: 'Ungewertete Partie.',
  login_failed: 'Anmeldung nicht möglich.',
  feedback_empty: 'Schreib eine Nachricht oder gib eine Bewertung ab.',
  feedback_rate_limited: 'Danke! Du hast schon mehrere Nachrichten geschickt: Versuch es etwas später noch einmal.',
  tournament_unknown: 'Unbekannter Turniercode.',
  tournament_started: 'Dieses Turnier hat bereits begonnen.',
  tournament_full: 'Dieses Turnier ist voll.',
  tournament_not_host: 'Das kann nur der Veranstalter.',
  tournament_not_open: 'Die Anmeldung für dieses Turnier ist geschlossen.',
  tournament_needs_players: 'Zum Starten des Turniers braucht es mindestens 2 Spieler.',
  tournament_over: 'Dieses Turnier ist beendet.',
  tournament_too_many: 'Du veranstaltest bereits zu viele laufende Turniere: Beende oder sag zuerst eines davon ab.',
  tournament_no_rematch: 'Im Turnier gibt es keine Revanche: Weiter geht es über den Turnierbaum.',
}

export const de: LocaleTexts = {
  cardColors,
  cardKinds,
  cardLabel,
  winReasons,
  log,
  errors,
}
